import { del, get, set } from 'idb-keyval';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';

export const maxCacheBytes = 50 * 1024 * 1024;
export const maxCacheAgeMs = 7 * 24 * 60 * 60 * 1000;

const prefix = 'qs-cache-';

function storageKey(userId: string): string {
  return `${prefix}${userId}`;
}

export function trimClient(client: PersistedClient, limit = maxCacheBytes): PersistedClient {
  const queries = [...client.clientState.queries].sort(
    (a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt,
  );
  const kept: typeof queries = [];
  let size = 0;
  for (const q of queries) {
    if (Date.now() - q.state.dataUpdatedAt > maxCacheAgeMs) continue;
    const bytes = JSON.stringify(q.state.data ?? null).length * 2;
    if (size + bytes > limit) continue;
    size += bytes;
    kept.push(q);
  }
  return { ...client, clientState: { ...client.clientState, queries: kept, mutations: [] } };
}

interface PendingWrite {
  timer: ReturnType<typeof setTimeout> | null;
  value: PersistedClient | null;
}

const pendingWrites = new Map<string, PendingWrite>();
let flushHooked = false;

function flushKey(key: string): void {
  const slot = pendingWrites.get(key);
  if (!slot) return;
  if (slot.timer) clearTimeout(slot.timer);
  slot.timer = null;
  const value = slot.value;
  slot.value = null;
  if (value) void set(key, trimClient(value)).catch(() => undefined);
}

export function flushPersisters(): void {
  for (const key of pendingWrites.keys()) flushKey(key);
}

function hookFlush(): void {
  if (flushHooked || typeof window === 'undefined') return;
  flushHooked = true;
  window.addEventListener('pagehide', flushPersisters);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPersisters();
  });
}

export function createPersister(userId: string): Persister {
  const key = storageKey(userId);
  hookFlush();
  return {
    persistClient: async (client) => {
      const slot = pendingWrites.get(key) ?? { timer: null, value: null };
      pendingWrites.set(key, slot);
      slot.value = client;
      if (slot.timer) return;
      slot.timer = setTimeout(() => flushKey(key), 1000);
    },
    restoreClient: async () => {
      try {
        return (await get<PersistedClient>(key)) ?? undefined;
      } catch {
        return undefined;
      }
    },
    removeClient: async () => {
      const slot = pendingWrites.get(key);
      if (slot?.timer) clearTimeout(slot.timer);
      pendingWrites.delete(key);
      await del(key).catch(() => undefined);
    },
  };
}

export async function clearAllCaches(): Promise<void> {
  try {
    const { keys } = await import('idb-keyval');
    for (const k of await keys()) if (typeof k === 'string' && k.startsWith(prefix)) await del(k);
  } catch {
    return;
  }
}
